import React, { useLayoutEffect, useMemo, useRef, useState } from 'react';

export interface ChartDataPoint {
  label: string;
  value: number; // 0-100
  /** ISO-ish timestamp — when present, dots are positioned by time so gaps
   *  between points reflect the real interval (and not just array index). */
  submitted_at?: string;
}

interface PerformanceChartProps {
  data: ChartDataPoint[];
  color?: string;
  /** Unique ID suffix for gradient defs (needed if multiple charts on page) */
  id?: string;
  /** Chart height in px */
  height?: number;
  /** Show dashed grid lines */
  dashedGrid?: boolean;
  /** Stroke width for the line (capped internally for a consistent thin look) */
  strokeWidth?: number;
  /** Optional fixed time domain (ms). When set, dots and labels are placed
   *  within this window instead of min/max of the data — keeps Bu Hafta
   *  anchored to the full 7-day window even if only one attempt exists. */
  domainMin?: number;
  domainMax?: number;
  /** Top of the Y axis. Defaults to 100 (percentage / başarı puanı). Pass a
   *  count (e.g. daily question totals) to plot raw values instead of 0-100. */
  yMax?: number;
}

const PerformanceChart: React.FC<PerformanceChartProps> = ({
  data,
  color = '#3973bf',
  id = 'perf',
  height = 329,
  dashedGrid = true,
  strokeWidth = 2.5,
  domainMin,
  domainMax,
  yMax,
}) => {
  // Percentage charts top out at 100; count charts (Soru Sayısı) pass an
  // explicit yMax so the axis scales to the data.
  const axisMax = typeof yMax === 'number' && yMax > 0 ? yMax : 100;
  // Extra headroom on top so a dot at value=100 (+ its stroke) doesn't clip.
  const padding = { top: 48, right: 20, bottom: 40, left: 50 };

  const svgRef = useRef<SVGSVGElement>(null);
  // Match the viewBox width to the rendered width so the chart fills its
  // container instead of letterboxing (empty gaps left/right) on wide screens.
  const [measuredW, setMeasuredW] = useState(0);
  useLayoutEffect(() => {
    const el = svgRef.current;
    if (!el) return;
    const update = () => setMeasuredW(el.getBoundingClientRect().width || 0);
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const viewW = measuredW > 0 ? measuredW : 800;
  const viewH = height;
  const chartW = viewW - padding.left - padding.right;
  const chartH = viewH - padding.top - padding.bottom;
  // A single thin weight reads far cleaner than the old 4–6px "marker" line.
  // Cap whatever a caller passes so every chart on the platform matches.
  const lineWidth = Math.min(strokeWidth, 2.75);

  // Six labels evenly spaced from axisMax down to 0 (rounded to integers).
  const yLabels = useMemo(
    () => Array.from({ length: 6 }, (_, i) => Math.round((axisMax * (5 - i)) / 5)),
    [axisMax],
  );

  const [hoverIdx, setHoverIdx] = useState<number | null>(null);

  // Sort data by submitted_at so the line never goes left-to-right backwards.
  const sortedData = useMemo(() => {
    if (data.length < 2) return data;
    return [...data].sort((a, b) => {
      if (!a.submitted_at || !b.submitted_at) return 0;
      return new Date(a.submitted_at).getTime() - new Date(b.submitted_at).getTime();
    });
  }, [data]);

  // Convert data points to SVG coordinates. When submitted_at is available,
  // position dots proportionally in real time. If the parent passes an explicit
  // domain (domainMin/Max), use that — otherwise fall back to data min/max.
  const points = useMemo(() => {
    if (sortedData.length === 0) return [];
    const times = sortedData.map((d) => (d.submitted_at ? new Date(d.submitted_at).getTime() : NaN));
    const haveAllTimes = times.every((t) => !Number.isNaN(t));
    const domainOk = typeof domainMin === 'number' && typeof domainMax === 'number' && domainMax > domainMin;
    // An explicit domain is NOT on its own enough to place points in time —
    // the points must actually carry timestamps. Both Performansım pages render
    // a 7-point {label:'Gün N', value:0} placeholder when the student has no
    // activity in the selected period/ders, and those points have no
    // submitted_at. With `domainOk` alone deciding this, every x came out NaN
    // and the chart emitted `d="MNaN,280 CNaN,NaN…"` — an invalid path, logged
    // as an SVG error on what is a completely ordinary empty state (new
    // student, or any ders they have not worked on yet).
    const useTime = haveAllTimes && (domainOk || sortedData.length > 1);
    const tMin = domainOk ? (domainMin as number) : (haveAllTimes ? Math.min(...times) : 0);
    const tMax = domainOk ? (domainMax as number) : (haveAllTimes ? Math.max(...times) : 1);
    const span = tMax - tMin || 1;
    return sortedData.map((d, i) => {
      const x = useTime
        ? padding.left + ((times[i] - tMin) / span) * chartW
        : padding.left + (sortedData.length > 1 ? (i / (sortedData.length - 1)) * chartW : chartW / 2);
      return {
        x,
        y: padding.top + chartH - (Math.max(0, Math.min(axisMax, d.value)) / axisMax) * chartH,
      };
    });
  }, [sortedData, chartW, chartH, padding.left, padding.top, domainMin, domainMax, axisMax]);

  // Monotone cubic interpolation (à la D3 curveMonotoneX): smooth, but the curve
  // never overshoots past a data point — so a sharp drop no longer dips below 0
  // or bulges above 100 the way the old Catmull-Rom spline did.
  const buildPath = useMemo(() => {
    const n = points.length;
    if (n < 2) return '';
    // Belt and braces: never emit a path containing NaN/Infinity. Any future
    // caller passing a malformed point should get no line, not a broken `d`.
    if (!points.every((p) => Number.isFinite(p.x) && Number.isFinite(p.y))) return '';
    const dx: number[] = [];
    const slope: number[] = [];
    for (let i = 0; i < n - 1; i++) {
      const h = points[i + 1].x - points[i].x;
      dx[i] = h;
      slope[i] = Math.abs(h) < 1e-6 ? 0 : (points[i + 1].y - points[i].y) / h;
    }
    const tan: number[] = new Array(n);
    tan[0] = slope[0];
    tan[n - 1] = slope[n - 2];
    for (let i = 1; i < n - 1; i++) {
      if (slope[i - 1] * slope[i] <= 0) {
        // Local min/max → flat tangent keeps the curve from overshooting.
        tan[i] = 0;
      } else {
        const w1 = 2 * dx[i] + dx[i - 1];
        const w2 = dx[i] + 2 * dx[i - 1];
        tan[i] = (w1 + w2) / (w1 / slope[i - 1] + w2 / slope[i]);
      }
    }
    let path = `M${points[0].x},${points[0].y}`;
    for (let i = 0; i < n - 1; i++) {
      const h = dx[i];
      const { x: x1, y: y1 } = points[i];
      const { x: x2, y: y2 } = points[i + 1];
      path += ` C${x1 + h / 3},${y1 + (tan[i] * h) / 3} ${x2 - h / 3},${y2 - (tan[i + 1] * h) / 3} ${x2},${y2}`;
    }
    return path;
  }, [points]);

  // Closed path for gradient fill area
  const fillPath = useMemo(() => {
    if (!buildPath) return '';
    const bottomY = padding.top + chartH;
    return `${buildPath} L${points[points.length - 1].x},${bottomY} L${points[0].x},${bottomY} Z`;
  }, [buildPath, points, padding.top, chartH]);

  const gradientId = `grad-${id}`;
  const shadowId = `shadow-${id}`;

  // Map a client X coordinate to the nearest data point (works regardless of how
  // the viewBox is scaled/letterboxed, via the SVG's screen CTM).
  const handleMove = (clientX: number) => {
    const svg = svgRef.current;
    if (!svg || points.length === 0) return;
    const ctm = svg.getScreenCTM();
    if (!ctm) return;
    const pt = svg.createSVGPoint();
    pt.x = clientX;
    pt.y = 0;
    const ux = pt.matrixTransform(ctm.inverse()).x;
    let best = 0;
    let bestD = Infinity;
    for (let i = 0; i < points.length; i++) {
      const d = Math.abs(points[i].x - ux);
      if (d < bestD) { bestD = d; best = i; }
    }
    setHoverIdx(best);
  };

  return (
    <svg
      ref={svgRef}
      viewBox={`0 0 ${viewW} ${viewH}`}
      className="w-full h-full"
      preserveAspectRatio="xMidYMid meet"
      onMouseMove={(e) => handleMove(e.clientX)}
      onMouseLeave={() => setHoverIdx(null)}
      onTouchStart={(e) => { if (e.touches[0]) handleMove(e.touches[0].clientX); }}
      onTouchMove={(e) => { if (e.touches[0]) handleMove(e.touches[0].clientX); }}
      onTouchEnd={() => setHoverIdx(null)}
    >
      <defs>
        <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.28" />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </linearGradient>
        <filter id={shadowId} x="-20%" y="-20%" width="140%" height="140%">
          <feDropShadow dx="0" dy="2" stdDeviation="3" floodColor={color} floodOpacity="0.22" />
        </filter>
      </defs>

      {/* Grid lines */}
      {yLabels.map((label, i) => {
        const y = padding.top + (i / (yLabels.length - 1)) * chartH;
        return (
          <line
            key={i}
            x1={padding.left}
            y1={y}
            x2={padding.left + chartW}
            y2={y}
            stroke="#2b2b2b"
            strokeWidth={dashedGrid ? 1.5 : 1}
            strokeDasharray={dashedGrid ? '8 8' : undefined}
            opacity={0.12}
          />
        );
      })}

      {/* Y-axis labels */}
      {yLabels.map((label, i) => {
        const y = padding.top + (i / (yLabels.length - 1)) * chartH;
        return (
          <text
            key={i}
            x={padding.left - 12}
            y={y + 5}
            fill="#2b2b2b"
            fontSize="14"
            fontFamily="Inter"
            fontWeight="600"
            textAnchor="end"
          >
            {label}
          </text>
        );
      })}

      {/* X-axis labels — 5 evenly-spaced labels across the chart width. */}
      {(() => {
        const MAX_LABELS = 5;
        const n = sortedData.length;
        if (n === 0) return null;
        const times = sortedData.map((d) => (d.submitted_at ? new Date(d.submitted_at).getTime() : NaN));
        const haveAllTimes = times.every((t) => !Number.isNaN(t));
        const explicitDomain = typeof domainMin === 'number' && typeof domainMax === 'number' && domainMax > domainMin;
        const useTime = explicitDomain || haveAllTimes;
        const tMin = explicitDomain ? (domainMin as number) : (haveAllTimes ? Math.min(...times) : 0);
        const tMax = explicitDomain ? (domainMax as number) : (haveAllTimes ? Math.max(...times) : n - 1);
        const fmt = (ms: number) => new Date(ms).toLocaleDateString('tr-TR', { day: '2-digit', month: 'short' });
        return Array.from({ length: MAX_LABELS }, (_, k) => {
          const frac = k / (MAX_LABELS - 1);
          const x = padding.left + frac * chartW;
          const text = useTime
            ? fmt(tMin + frac * (tMax - tMin))
            : (sortedData[Math.round(frac * (n - 1))]?.label ?? '');
          const anchor: 'start' | 'middle' | 'end' = k === 0 ? 'start' : k === MAX_LABELS - 1 ? 'end' : 'middle';
          return (
            <text key={k} x={x} y={viewH - 5} fill="#2b2b2b" fontSize="14" fontFamily="Inter" fontWeight="600" textAnchor={anchor}>
              {text}
            </text>
          );
        });
      })()}

      {/* Gradient fill */}
      {fillPath && <path d={fillPath} fill={`url(#${gradientId})`} />}

      {/* Line */}
      {buildPath && (
        <path
          d={buildPath}
          fill="none"
          stroke={color}
          strokeWidth={lineWidth}
          strokeLinecap="round"
          strokeLinejoin="round"
          filter={`url(#${shadowId})`}
        />
      )}

      {/* A lone attempt has no line to sit on — show its dot so it's visible. */}
      {points.length === 1 && <circle cx={points[0].x} cy={points[0].y} r={4} fill={color} />}

      {/* Hover: vertical guide + highlighted point + tooltip (score over date). */}
      {hoverIdx != null && points[hoverIdx] && (() => {
        const p = points[hoverIdx];
        const d = sortedData[hoverIdx];
        const score = Math.round(d.value);
        const dateStr = d.submitted_at
          ? new Date(d.submitted_at).toLocaleDateString('tr-TR', { day: '2-digit', month: 'short', year: 'numeric' })
          : d.label;
        const boxW = 96;
        const boxH = 48;
        let bx = p.x - boxW / 2;
        bx = Math.max(4, Math.min(viewW - boxW - 4, bx));
        let by = p.y - boxH - 16;
        if (by < 4) by = p.y + 18; // flip below the point when too close to the top
        return (
          <g pointerEvents="none">
            <line
              x1={p.x}
              y1={padding.top}
              x2={p.x}
              y2={padding.top + chartH}
              stroke={color}
              strokeWidth={1.25}
              strokeDasharray="4 5"
              opacity={0.45}
            />
            <circle cx={p.x} cy={p.y} r={9} fill={color} opacity={0.18} />
            <circle cx={p.x} cy={p.y} r={5} fill="#ffffff" stroke={color} strokeWidth={2.5} />
            <rect x={bx} y={by} width={boxW} height={boxH} rx={10} fill="#2b2b2b" opacity={0.95} />
            <text x={bx + boxW / 2} y={by + 21} fill="#ffffff" fontSize="18" fontFamily="Inter" fontWeight="700" textAnchor="middle">
              {score}
            </text>
            <text x={bx + boxW / 2} y={by + 38} fill="#cbd5e1" fontSize="11.5" fontFamily="Inter" textAnchor="middle">
              {dateStr}
            </text>
          </g>
        );
      })()}
    </svg>
  );
};

export default PerformanceChart;
